import { useEffect, useState } from 'react';
import { useHistory } from 'react-router-dom';
import { useLeasingUrlParams } from '../../hooks/useLeasingUrlParams';

import {
  Alert,
  Anchor,
  Box,
  Button,
  Card,
  FormField,
  FormHelp,
  Heading,
  Icon,
  Label,
  MediaObject,
  Paragraph,
  Tag,
  Text,
  TextButton,
  Toast,
  ToggleCard,
  useToast,
} from '@zillow/constellation-10';
import { IconFileFilled, IconHandshakeFilled, IconLaptopFilled } from '@zillow/constellation-icons';
import { LoadingHeading, LoadingParagraph } from '@zg-rentals/loading-components';
import { LeaseStatus } from '@zg-rentals/ts-rental-manager-api-client';
import { useRentalManagerUser } from '@zg-rentals/rental-manager-env';
import { LeaseType } from '@zg-rentals/ts-leasing-client';
import FaqAccordion from '../../components/Widgets/FaqAccordion';
import {
  IS_LEASE_BUILDER_ENABLED,
  ZILLOW_LEASES_HELP,
  ZILLOW_RENTAL_MANAGER_SUBMIT_REQUEST,
} from '../../constants/stringConstants';
import { openStaticAsset } from '../../utils/staticAsssets';
import { Lease } from '../../models';
import {
  emitButtonToContinueStartSelfUploadEvent,
  emitButtonToContinueStartSignedSelfUploadEvent,
  emitButtonToContinueStartZillowLeaseEvent,
  emitCreateLeaseContinueEvent,
  emitViewCreateLeaseEvent,
  emitViewSampleLeaseEvent,
} from '../../../analytics/events';
import {
  useApplicantsForApplicationQuery,
  useCreateNewLeaseForApplicationMutation,
  useCreateNewLeaseMutation,
  useLeaseSupportForPropertyQuery,
  useWebAnalyticsForListingQuery,
} from '../../utils/queries';

function CreateLease() {
  const { propertyId } = useLeasingUrlParams();
  const history = useHistory();
  const { user } = useRentalManagerUser();
  const { enqueueToast } = useToast();
  const webAnalyticsQuery = useWebAnalyticsForListingQuery(propertyId, { enabled: !!propertyId });
  const zrm_listing_info = webAnalyticsQuery.data;

  const queryParameters = new URLSearchParams(location.search);
  const applicationId = queryParameters.get('applicationId');

  const leaseSupportInfoQuery = useLeaseSupportForPropertyQuery(propertyId);
  const leaseSupportInfo = leaseSupportInfoQuery.data;

  const applicantsQuery = useApplicantsForApplicationQuery(propertyId, applicationId, {
    enabled: !!applicationId,
  });
  const applicants = applicantsQuery.data?.applicants || [];

  const createNewLeaseMutation = useCreateNewLeaseMutation();
  const createNewLeaseForApplicationMutation = useCreateNewLeaseForApplicationMutation();

  useEffect(() => {
    if (createNewLeaseMutation.isError || createNewLeaseForApplicationMutation.isError) {
      enqueueToast(
        <Toast
          tone="critical"
          actionButton={
            <TextButton asChild onImpact>
              <a href={ZILLOW_RENTAL_MANAGER_SUBMIT_REQUEST} target="_blank" rel="noopener noreferrer">
                Contact support
              </a>
            </TextButton>
          }
        >
          Lease couldn’t be created. Your listing may not support this lease type.
        </Toast>,
      );
    }
  }, [createNewLeaseMutation.isError, createNewLeaseForApplicationMutation.isError, enqueueToast]);

  const FAQ_SECTIONS = [
    {
      label: 'Uploading a lease for signing',
      description: (
        <>
          <Heading
            level={3}
            css={{ textStyle: 'responsive.body-lg-bold', color: 'text.subtle', marginBottom: 'tighter' }}
          >
            Step 1
          </Heading>
          <Heading level={4} css={{ textStyle: 'responsive.body-lg-bold' }}>
            Ensure documents are ready to be signed
          </Heading>
          <Paragraph css={{ marginBottom: 'tight' }}>
            Make any changes before uploading. Be sure you have the names and email addresses of the people signing.
          </Paragraph>
          <Heading
            level={3}
            css={{ color: 'text.subtle', marginBottom: 'tighter', textStyle: 'responsive.body-lg-bold' }}
          >
            Step 2
          </Heading>
          <Heading level={4} css={{ textStyle: 'responsive.body-lg-bold' }}>
            Sign electronically
          </Heading>
          <Paragraph>
            Mark where everyone should sign, then share with your renters. Everyone will sign online. We’ll help you
            track the progress.
          </Paragraph>
        </>
      ),
    },
    {
      label: 'Storing a previously signed lease',
      description: (
        <>
          <Heading
            level={3}
            css={{ textStyle: 'responsive.body-lg-bold', color: 'text.subtle', marginBottom: 'tighter' }}
          >
            Step 1
          </Heading>
          <Heading level={4} css={{ textStyle: 'responsive.body-lg-bold' }}>
            Ensure documents are already signed
          </Heading>
          <Paragraph css={{ marginBottom: 'tight' }}>
            Be sure you have the names and email addresses of the people who have signed.
          </Paragraph>
          <Heading
            level={3}
            css={{ textStyle: 'responsive.body-lg-bold', color: 'text.subtle', marginBottom: 'tighter' }}
          >
            Step 2
          </Heading>
          <Heading level={4} css={{ textStyle: 'responsive.body-lg-bold' }}>
            View your lease
          </Heading>
          <Paragraph>
            Once you fill in some basic information, your signed lease will be viewable within Zillow.
          </Paragraph>
        </>
      ),
    },
  ];

  const APPLICATION_FAQ_SUPPLEMENT = [
    {
      label: 'How do I add, remove, or change my tenants?',
      description: (
        <Text>
          Once your lease is created, you can add, remove, or change the tenants in your lease. There is a limit of 8
          tenants for a lease. You can also choose another application to create a lease from.
        </Text>
      ),
    },
  ];

  const ZILLOW_LEASE_FAQ_SUPPLEMENT = [
    {
      label: 'How lease builder works',
      description: (
        <>
          <Heading
            level={3}
            css={{ textStyle: 'responsive.body-lg-bold', color: 'text.subtle', marginBottom: 'tighter' }}
          >
            Step 1
          </Heading>
          <Heading level={4} css={{ textStyle: 'responsive.body-lg-bold' }}>
            Create your lease
          </Heading>
          <Paragraph css={{ marginBottom: 'tight' }}>
            Answer a few questions about the property, your preferences, and who will be covered by the lease.
          </Paragraph>
          <Heading
            level={3}
            css={{ textStyle: 'responsive.body-lg-bold', color: 'text.subtle', marginBottom: 'tighter' }}
          >
            Step 2
          </Heading>
          <Heading level={4} css={{ textStyle: 'responsive.body-lg-bold' }}>
            Review & share
          </Heading>
          <Paragraph css={{ marginBottom: 'tighter' }}>
            We’ll help you produce a shareable lease that your renters and co-landlords can easily review.{' '}
            {leaseSupportInfo?.explainerLeaseName && (
              <Anchor onClick={onLeaseExplainerClick}>See a sample lease</Anchor>
            )}
          </Paragraph>
          <Heading
            level={3}
            css={{ textStyle: 'responsive.body-lg-bold', color: 'text.subtle', marginBlock: 'tighter' }}
          >
            Step 3
          </Heading>
          <Heading level={4} css={{ textStyle: 'responsive.body-lg-bold' }}>
            Sign online
          </Heading>
          <Paragraph css={{ marginBottom: 'tight' }}>
            Save time and trouble by getting your lease signed online, or quickly download it for an in-person signing.
          </Paragraph>
          <TextButton asChild>
            <a target="_blank" rel="noopener noreferrer" href={ZILLOW_LEASES_HELP}>
              Learn more
            </a>
          </TextButton>
        </>
      ),
    },
  ];

  const [leaseType, setLeaseType] = useState<LeaseType | null>(null);

  useEffect(() => {
    if (zrm_listing_info) {
      emitViewCreateLeaseEvent({ user, listing: zrm_listing_info });
    }
  }, [zrm_listing_info, user]);

  function onLeaseExplainerClick() {
    emitViewSampleLeaseEvent({ user, listing: zrm_listing_info });
    if (leaseSupportInfo.explainerLeaseName != null) {
      openStaticAsset(Lease.getExplainerLeasePath(leaseSupportInfo.explainerLeaseName));
    }
  }

  function generateFAQArray() {
    let faqArray = FAQ_SECTIONS;
    if (applicationId) {
      faqArray = faqArray.concat(APPLICATION_FAQ_SUPPLEMENT);
    }
    if (IS_LEASE_BUILDER_ENABLED && leaseSupportInfo?.isSupported) {
      faqArray = ZILLOW_LEASE_FAQ_SUPPLEMENT.concat(faqArray);
    }
    return faqArray;
  }

  async function onContinueClick() {
    const zrmLeaseInfo = {
      lease_type_cd: null,
      lease_status_txt: LeaseStatus.INCOMPLETE_DRAFT,
      lease_bundle_id: null,
    };

    let response;
    if (applicationId && applicants?.length > 0) {
      response = await createNewLeaseForApplicationMutation.mutateAsync({
        postData: {
          propertyId,
          applicationId,
          leaseType,
        },
      });

      emitCreateLeaseContinueEvent({ user, listing: zrm_listing_info });
    } else {
      response = await createNewLeaseMutation.mutateAsync({
        postData: {
          propertyId,
          leaseType,
        },
      });
    }

    if (response?.leaseId) {
      if (leaseType === LeaseType.SIGNED_SELF_UPLOAD) {
        zrmLeaseInfo.lease_type_cd = LeaseType.SIGNED_SELF_UPLOAD;
        emitButtonToContinueStartSignedSelfUploadEvent({
          user,
          listing: zrm_listing_info,
          zrmLeaseInfo,
        });
      } else if (leaseType === LeaseType.ZILLOW) {
        zrmLeaseInfo.lease_type_cd = LeaseType.ZILLOW;
        emitButtonToContinueStartZillowLeaseEvent({ user, listing: zrm_listing_info, zrmLeaseInfo });
      } else if (leaseType === LeaseType.SELF_UPLOAD) {
        zrmLeaseInfo.lease_type_cd = LeaseType.SELF_UPLOAD;
        emitButtonToContinueStartSelfUploadEvent({ user, listing: zrm_listing_info, zrmLeaseInfo });
      }
      history.push(Lease.getRootPath(response.leaseId));
    }
  }

  function generateZillowLeaseDescription(): React.ReactNode {
    const { supportedGeographyTitle } = leaseSupportInfo;

    if (!supportedGeographyTitle) {
      return 'Create a free custom lease that includes legal clauses specific to your area.';
    } else if (supportedGeographyTitle?.length === 1) {
      return (
        <>
          Create a free custom lease that includes legal clauses specific to{' '}
          <strong>{supportedGeographyTitle[0]}</strong>.
        </>
      );
    } else if (supportedGeographyTitle?.length === 2) {
      return (
        <>
          Create a free custom lease that includes legal clauses specific to{' '}
          <strong>{supportedGeographyTitle[0]}</strong> and <strong>{supportedGeographyTitle[1]}</strong>.
        </>
      );
    }
  }

  return (
    <>
      <Box css={{ display: 'grid', gridTemplateColumns: 'repeat(12, 1fr)', gap: 'loose' }}>
        <Box css={{ gridColumnEnd: { base: 'span 12', lg: 'span 8' } }}>
          <Heading level={2} css={{ textStyle: 'responsive.heading-sm', marginBottom: 'tighter' }}>
            Create and sign leases online
          </Heading>
          <Paragraph css={{ marginBottom: 'tight' }}>
            {IS_LEASE_BUILDER_ENABLED
              ? 'Build a new lease with Zillow’s tools, or upload your own to send for signing or storage.'
              : 'Upload your lease to send for signing or storage.'}
          </Paragraph>
          {applicants?.length > 0 && applicationId && (
            <FormField
              css={{ marginBottom: 'tight' }}
              label={
                <Label>
                  {applicants.length > 1 ? 'Here are the tenants you chose' : 'Here is the tenant you chose'}
                </Label>
              }
              control={
                <Card tone="neutral" elevated={false} css={{ padding: 'loose', marginBottom: 'tight' }}>
                  <Card.Body>
                    <Box css={{ display: 'grid', gridTemplateColumns: 'repeat(12, 1fr)' }}>
                      {applicants.map((applicant, index) => {
                        return (
                          <>
                            <Box
                              css={{
                                gridColumnEnd: { base: 'span 12', md: 'span 4' },
                                marginBottom: index + 1 !== applicants.length ? 'xs' : undefined,
                              }}
                            >
                              <Text css={{ fontWeight: 'bold' }}>{`Tenant #${index + 1}`}</Text>
                            </Box>
                            <Box
                              css={{
                                gridColumnEnd: { base: 'span 12', md: 'span 8' },
                                marginBottom: index + 1 !== applicants.length ? 'xs' : undefined,
                              }}
                            >
                              <Text>{`${applicant?.firstName} ${applicant?.lastName}`}</Text>
                              <Paragraph className={`tenant-email`} css={{ wordBreak: 'break-all' }}>
                                {applicant?.email}
                              </Paragraph>
                            </Box>
                          </>
                        );
                      })}
                    </Box>
                  </Card.Body>
                </Card>
              }
              description={<FormHelp>You can change, delete, or add tenants later on in the process</FormHelp>}
            />
          )}
          {applicants?.length === 0 && applicationId && !applicantsQuery.isPending && (
            <Alert tone="warning" css={{ marginBottom: 'tight' }}>
              We couldn’t load your applicants. You can still create a lease and manually add your applicants later.
            </Alert>
          )}
          {!leaseSupportInfoQuery.isLoading && !leaseSupportInfo ? null : (
            <Box
              role={leaseSupportInfo ? 'radiogroup' : undefined}
              aria-label={leaseSupportInfo ? 'Choose a lease type' : undefined}
            >
              {IS_LEASE_BUILDER_ENABLED && (
                <ToggleCard
                  tone="neutral"
                  elevated={false}
                  disabled={leaseSupportInfoQuery.isLoading || !leaseSupportInfo?.isSupported}
                  onClick={leaseSupportInfo?.isSupported ? () => setLeaseType(LeaseType.ZILLOW) : undefined}
                  selected={leaseType === LeaseType.ZILLOW}
                  css={{ padding: 'loose', marginBottom: 'tight' }}
                >
                  <Box css={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 'tight' }}>
                    <MediaObject
                      media={
                        <Icon role="img" size="lg" css={{ color: 'icon.accent.brand.hero' }}>
                          <IconLaptopFilled />
                        </Icon>
                      }
                    >
                      <LoadingHeading
                        isLoading={leaseSupportInfoQuery.isLoading}
                        level={3}
                        css={{ textStyle: 'responsive.heading-sm' }}
                        loadingWidth="60%"
                      >
                        <Box css={{ display: 'flex', alignItems: 'center', gap: 'tighter', flexWrap: 'wrap' }}>
                          Lease builder
                          {leaseSupportInfo?.isSupported && leaseSupportInfo?.isRefreshed && (
                            <Tag size="sm" tone="info">
                              Recently updated
                            </Tag>
                          )}
                          {leaseSupportInfo && !leaseSupportInfo.isSupported && (
                            <Tag size="sm" tone="green">
                              Coming soon
                            </Tag>
                          )}
                        </Box>
                      </LoadingHeading>
                      <LoadingParagraph isLoading={leaseSupportInfoQuery.isLoading} loadingWidth="85%">
                        {leaseSupportInfo?.isSupported
                          ? generateZillowLeaseDescription()
                          : 'Create a free custom lease that includes legal clauses specific to your area.'}
                      </LoadingParagraph>
                    </MediaObject>
                  </Box>
                </ToggleCard>
              )}
              <ToggleCard
                tone="neutral"
                elevated={false}
                disabled={leaseSupportInfoQuery.isLoading}
                onClick={!leaseSupportInfoQuery.isLoading ? () => setLeaseType(LeaseType.SELF_UPLOAD) : undefined}
                selected={leaseType === LeaseType.SELF_UPLOAD}
                css={{ padding: 'loose', marginBottom: 'tight' }}
              >
                <MediaObject
                  media={
                    <Icon role="img" size="lg" css={{ color: 'icon.accent.brand.hero' }}>
                      <IconHandshakeFilled />
                    </Icon>
                  }
                >
                  <LoadingHeading
                    isLoading={leaseSupportInfoQuery.isLoading}
                    level={3}
                    css={{ textStyle: 'responsive.heading-sm' }}
                    loadingWidth="50%"
                  >
                    Share a lease for signing
                  </LoadingHeading>
                  <LoadingParagraph isLoading={leaseSupportInfoQuery.isLoading} loadingWidth="75%">
                    Upload your own lease to send for signing online.
                  </LoadingParagraph>
                </MediaObject>
              </ToggleCard>
              <ToggleCard
                tone="neutral"
                elevated={false}
                disabled={leaseSupportInfoQuery.isLoading}
                onClick={
                  !leaseSupportInfoQuery.isLoading ? () => setLeaseType(LeaseType.SIGNED_SELF_UPLOAD) : undefined
                }
                selected={leaseType === LeaseType.SIGNED_SELF_UPLOAD}
                css={{ padding: 'loose', marginBottom: 'tight' }}
              >
                <MediaObject
                  media={
                    <Icon role="img" size="lg" css={{ color: 'icon.accent.brand.hero' }}>
                      <IconFileFilled />
                    </Icon>
                  }
                >
                  <LoadingHeading
                    isLoading={leaseSupportInfoQuery.isLoading}
                    level={3}
                    css={{ textStyle: 'responsive.heading-sm' }}
                    loadingWidth="55%"
                  >
                    Store your signed lease
                  </LoadingHeading>
                  <LoadingParagraph isLoading={leaseSupportInfoQuery.isLoading} loadingWidth="80%">
                    Keep all your property information stored in one place on Zillow.
                  </LoadingParagraph>
                </MediaObject>
              </ToggleCard>
            </Box>
          )}
          <Button
            tone="brand"
            emphasis="filled"
            disabled={
              leaseSupportInfoQuery.isLoading ||
              !leaseType ||
              createNewLeaseMutation.isPending ||
              createNewLeaseForApplicationMutation.isPending
            }
            loading={createNewLeaseMutation.isPending || createNewLeaseForApplicationMutation.isPending}
            onClick={onContinueClick}
          >
            Continue
          </Button>
        </Box>
        <Box css={{ gridColumnEnd: { base: 'span 12', lg: 'span 4' } }}>
          <FaqAccordion title="How to use our lease tools" sections={generateFAQArray()} />
        </Box>
      </Box>
    </>
  );
}

export default CreateLease;
